/* Copyright (c) 2020-present CloudIO Inc */

import { pageAtom } from 'designer/atoms';
import { genId } from 'designer/state';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
  getDataStoreItem,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { camelCase } from 'lodash-es';
import { isNullEqual } from 'utils/isEmpty';
import { Item } from '../../registry/Component.type';
import { getManager } from '../../registry/Manager';
import { getNextAlias } from '../DataStore/DataStoreManager';

export async function chartAnalyze({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId] as Item<'Chartjs'>;
  const { labelAttribute, valueAttribute, dsalias: alias } = item.props;

  if (!alias) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Data Store not setup!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  if (!(valueAttribute && labelAttribute)) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Missing value/label attribute!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  const dsItem = getDataStoreItem({ page, alias }) as Item<'ReadOnlyDataStore'>;

  if (!dsItem) {
    alerts.push({
      key: `missing-alias-${itemId}`,
      showItemId: itemId,
      alert: `Missing data store for alias ${alias}!`,
      itemId,
    });
    return [alerts, autoFixes];
  }

  let hasMajorIssue = false;
  const duplicateToReadonlyFix = () => {
    const _page = { ...platform.getPage(appUid, pageId) };
    const dataSource = platform.getDataSourceName(appUid, pageId, alias);
    if (!dataSource) {
      platform.showError(`Missing data source for alias ${alias}!`);
      return;
    }

    const map = platform.getDataSources(appUid, pageId);
    const newAlias = getNextAlias(`${camelCase(dataSource)}SelectAlias`, map);

    const ds: Item<'ReadOnlyDataStore'> = {
      children: [],
      type: 'ReadOnlyDataStore',
      parent: 'root',
      props: {
        dataSource,
        alias: newAlias,
        autoQuery: false,
        maxRows: 2000,
      },
      itemId: genId(_page),
    };
    const oldParent = _page.root;
    _page.root = {
      ...oldParent,
      children: [...oldParent.children, ds.itemId],
    };
    _page[ds.itemId] = ds;
    const updatedSelect: Item<'Chartjs'> = {
      ...item,
      props: { ...item.props, dsalias: newAlias },
    };
    _page[itemId] = updatedSelect;
    getManager(oldParent.type).onChildMoveIn({
      appUid,
      pageId,
      page: _page,
      oldValue: oldParent,
      value: _page.root,
      child: ds,
    });
    platform.setter(pageAtom, _page, {
      params: { appUid, pageId },
    });
  };
  const itemIds = Object.keys(page);
  for (let i = 0; i < itemIds.length; i++) {
    const id = itemIds[i];
    // eslint-disable-next-line no-continue
    if (id === itemId) continue;
    const itm = page[id];
    const _mgr = getManager(itm.type);
    if (_mgr.isDataSourceAliasUsed(itm, alias)) {
      if (_mgr.managerType === 'Component') {
        if (_mgr.type === 'Chartjs') {
          const _itm = itm as Item<'Chartjs'>;
          if (
            isNullEqual(item.props.valueAttribute, _itm.props.valueAttribute) &&
            isNullEqual(item.props.labelAttribute, _itm.props.labelAttribute) &&
            isNullEqual(
              item.props.orderByAttribute,
              _itm.props.orderByAttribute,
            ) &&
            isNullEqual(
              item.props.categoryAttribute,
              _itm.props.categoryAttribute,
            )
          ) {
            // it's ok
            continue;
          }
        }

        hasMajorIssue = true;

        alerts.push({
          key: `reuse-${itemId}`,
          fix: duplicateToReadonlyFix,
          showItemId: itemId,
          alert: `Data Store [${alias}] used by a ${mgr.name} is also used by another ${_mgr.name}! `,
          tip: `Chart component's data store must not be shared.

> Click \`Fix It\` to create a new Readonly Data Store for this a ${mgr.name}.`,
          itemId,
        });
        break;
      }
    }
  }

  if (!hasMajorIssue && dsItem.props.autoQuery) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const udpatedDs: Item<'ReadOnlyDataStore'> = {
        ...dsItem,
        props: { ...dsItem.props, autoQuery: false },
      };
      _page[dsItem.itemId] = udpatedDs;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `auto-${itemId}`,
      fix,
      showItemId: dsItem.itemId,
      alert: `Data Store [${alias}] used by a Chart has auto query on!`,
      tip: `Chart component controls the data store query, so turn off the auto query.

 > Click \`Fix It\` to turn off the auto query on data store with alias ${alias}.`,
      itemId,
    });
  }

  return [alerts, autoFixes];
}
